import clsx from 'clsx';
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControlLabel, Checkbox, Grid, ButtonBase } from '@mui/material';

import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';

import { ReactComponent as CloseSvg } from '../../../assets/svg/close.svg';
import styles from './Modal.module.scss';

const Modal = ({
  opened,
  children,
  headerContent,
  backBtnText,
  okBtnText,
  closeModalFunc,
  backFunc,
  okFunc,
  needAgreement,
  needButtons,
  okBtnDisabled,
  blockOutsideClick,
  containedStyle,
  scrollDisabled,
}) => {
  const [isAgreed, setIsAgreed] = useState(!needAgreement);
  const [isCheckboxEnabled, setIsCheckboxEnabled] = useState(false);
  const contentEl = useRef(null);

  const onCloseClick = () => {
    if (!blockOutsideClick) closeModalFunc();
  };

  const onBackClick = () => {
    closeModalFunc();
    backFunc();
  };

  const onOkClick = () => {
    closeModalFunc();
    okFunc();
  };

  const scrolledToEnd = (e) => {
    const { target } = e;

    setIsCheckboxEnabled(target.offsetHeight + Math.ceil(target.scrollTop) >= target.scrollHeight);
  };

  useEffect(() => {
    const { current } = contentEl;

    setIsCheckboxEnabled(
      current ? current.offsetHeight + Math.ceil(current.scrollTop) >= current.scrollHeight : false
    );
  }, [opened]);

  return (
    <div className={clsx(styles.modal, opened && styles.opened)}>
      <div className={styles.cover} onClick={onCloseClick} />
      <div className={styles.container}>
        <div className={styles.headerContainer}>
          <span className={styles.header}>{headerContent}</span>
          <Button variant="text" className={styles.closeBtn} onClick={onCloseClick}>
            <CloseSvg className={styles.closeSvg} />
          </Button>
        </div>
        <div
          className={clsx(styles.content, !scrollDisabled && styles.scrolled)}
          ref={contentEl}
          onScroll={scrolledToEnd}
        >
          {children}
        </div>
        {(needAgreement || needButtons) && (
          <div className={styles.agreeCheck}>
            {needAgreement && (
              <div className={styles.checkboxContainer}>
                <FormControlLabel
                  className={styles.checkbox}
                  control={
                    <Checkbox
                      onChange={() => setIsAgreed(!isAgreed)}
                      checkedIcon={<CheckBoxOutlined />}
                      variant="unlighted"
                      disabled={!isCheckboxEnabled}
                    />
                  }
                  label="I have read the terms and conditions and agree to them"
                  labelPlacement="end"
                />
              </div>
            )}
            {needButtons && (
              <Grid
                item
                container
                alignItems="center"
                justifyContent="space-between"
                className={styles.buttonCont}
              >
                {backBtnText && (
                  <ButtonBase
                    className={styles.backBtn}
                    variant="outlined"
                    component="label"
                    onClick={onBackClick}
                  >
                    {backBtnText}
                  </ButtonBase>
                )}
                <ButtonBase
                  className={clsx(
                    styles.okBtn,
                    ((needAgreement && !isAgreed) || okBtnDisabled) && styles.disabled
                  )}
                  variant={containedStyle || backBtnText ? 'contained' : 'text'}
                  onClick={() => onOkClick()}
                  component="label"
                  disabled={(needAgreement && !isAgreed) || okBtnDisabled}
                >
                  {okBtnText}
                </ButtonBase>
              </Grid>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

Modal.propTypes = {
  opened: PropTypes.bool,
  children: PropTypes.node,
  headerContent: PropTypes.string,
  backBtnText: PropTypes.string,
  okBtnText: PropTypes.string,
  closeModalFunc: PropTypes.func,
  backFunc: PropTypes.func,
  okFunc: PropTypes.func,
  needAgreement: PropTypes.bool,
  needButtons: PropTypes.bool,
  okBtnDisabled: PropTypes.bool,
  containedStyle: PropTypes.bool,
  blockOutsideClick: PropTypes.bool,
};

Modal.defaultProps = {
  opened: false,
  children: null,
  headerContent: '',
  backBtnText: '',
  okBtnText: '',
  closeModalFunc: () => {},
  backFunc: () => {},
  okFunc: () => {},
  needAgreement: false,
  needButtons: true,
  okBtnDisabled: false,
  containedStyle: false,
  blockOutsideClick: false,
};

export default Modal;
